import * as React from 'react';
import { jsx, css } from '@emotion/react';
import { mediaQueries, autoResponsiveClamp } from 'Src/me/pages/guest/proposals/new/React/viewHelpers';
import { sendGa4Event, setTime, wait } from 'Src/me/pages/guest/proposals/new/utils';
import { MagnifyingGlassSvg } from './assets/MagnifyingGlass.svg';
import { FREE_WORD_LIST } from './consts/freeWordList';
import { CrossMarkSvg } from './assets/CrossMark.svg';
import { MainvisualPropsType } from '../Mainvisual';

const { tablet, mobile } = mediaQueries();

const KEY_NAME = 'MainvisualFreeWordSearchWidget';
const PARAM_NAME = 'preferredSearchCriteria';

type MainvisualFreeWordSearchWidgetPropsType = MainvisualPropsType;

export const MainvisualFreeWordSearchWidget = (props: MainvisualFreeWordSearchWidgetPropsType) => {
  const { userAnswers, commonAnswerHandler, onApplyHandler: onApplyHandlerFromProps } = props;
  const [isSearchButtonDisabled, setIsSearchButtonDisabled] = React.useState(true);
  const [freeWordList, setFreeWordList] = React.useState(FREE_WORD_LIST);
  const [selectedFreeWordList, setSelectedFreeWordList] = React.useState<string[]>([]);
  const dialogModalRef = React.useRef<HTMLDialogElement>(null);

  // モーダルを開く処理
  const openModalHandler = React.useCallback(() => {
    if (!dialogModalRef.current) return;

    dialogModalRef.current.showModal();
    dialogModalRef.current.setAttribute('data-activate', 'true');
  }, []);

  // モーダルを閉じる処理
  const closeModalHandler = React.useCallback(async () => {
    if (!dialogModalRef.current) return;

    dialogModalRef.current.setAttribute('data-activate', 'false');
    await wait(0.3);
    dialogModalRef.current.close();
  }, []);

  const overlayClickHandler = React.useCallback((event: React.MouseEvent<HTMLDialogElement>) => {
    if (event.target === event.currentTarget) {
      closeModalHandler();
    }
  }, [closeModalHandler]);

  const freeWordChangeHandler = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const currentTargetValue = event.currentTarget?.value;

    if (!currentTargetValue) return;

    setFreeWordList((prev) => prev.map((freeWord) => {
      if (freeWord.value === currentTargetValue) {
        return {
          ...freeWord,
          checked: !freeWord.checked,
        };
      }
      return freeWord;
    }));
  }, []);

  const onApplyHandler = React.useCallback(() => {
    commonAnswerHandler(PARAM_NAME, selectedFreeWordList.join(', ') || '');

    userAnswers.push({
      keyName: 'mainvisualFreeWordSearchWidget',
      paramName: PARAM_NAME,
      question: '重視したい業者の絞り込み条件は',
      answer: selectedFreeWordList.join(',') || '',
      value: selectedFreeWordList.join(',') || '',
    });

    onApplyHandlerFromProps();
  }, [commonAnswerHandler, onApplyHandlerFromProps, selectedFreeWordList, userAnswers]);

  React.useEffect(() => {
    setIsSearchButtonDisabled(selectedFreeWordList.length === 0);
  }, [selectedFreeWordList]);

  return (
    <div
      css={css`
        max-width: 570px;
        height: 54px;
        margin-inline: auto;
        position: relative;

        ${mobile(({ setAutomaticDesignBasedSize }) => css`
          height: ${setAutomaticDesignBasedSize(54, 54)};
        `)}
      `}
    >
      <input
        type="text"
        readOnly
        placeholder="気になるキーワードを選択してください"
        value={selectedFreeWordList.join('｜')}
        onClick={openModalHandler}
        id={`ga_event_${KEY_NAME}_open_modal`}
        css={css`
          color: #000;
          font-size: 16px;
          font-weight: bold;
          width: 100%;
          height: 100%;
          padding: 16px 20px;
          background-color: #fff7c9;
          border: 2px solid #e8e8e8;
          border-radius: 8px;
          outline: none;
          cursor: pointer;

          ${mobile(({ setAutomaticDesignBasedSize }) => css`
            font-size: ${setAutomaticDesignBasedSize(16, 15)};
            padding: ${setAutomaticDesignBasedSize(16, 16)} ${setAutomaticDesignBasedSize(20, 14)};
          `)}

          &:placeholder-shown {
            color: #9f9f9f;
            font-weight: normal;
            background-color: #fff;
          }
        `}
      />
      <div
        css={css`
          height: 100%;
          position: absolute;
          right: 0;
          top: 0;

          ${isSearchButtonDisabled && css`
            pointer-events: none;
          `}
        `}
      >
        <div
          css={css`
            height: 100%;
            position: relative;
          `}
        >
          <div
            css={css`
              position: absolute;
              bottom: calc(100% + 12px);
              left: 50%;
              transform: translateX(-50%);
              transition: opacity ${setTime(0.4)}s ${setTime(0.4)}s, bottom ${setTime(0.4)}s ${setTime(0.4)}s;

              ${isSearchButtonDisabled && css`
                opacity: 0;
                bottom: calc(100% + 2px);
              `}
            `}
          >
            <p
              css={css`
                font-size: 16px;
                font-weight: bold;
                padding: 1px 13px;
                background-color: #ffd803;
                border-radius: 79px;
                position: relative;

                ${mobile(({ setAutomaticDesignBasedSize }) => css`
                  font-size: ${setAutomaticDesignBasedSize(16, 16)};
                `)}

                &::before {
                  content: "";
                  width: 14px;
                  height: 7px;
                  background-color: #ffd803;
                  clip-path: polygon(100% 0, 0 0, 50% 100%);
                  position: absolute;
                  top: 100%;
                  left: 50%;
                  transform: translateX(-50%);
                }
              `}
            >
              GO！
            </p>
          </div>
          <button
            type="button"
            onClick={onApplyHandler}
            id={`ga_event_${KEY_NAME}_search_button`}
            css={css`
              width: 80px;
              height: 100%;
              display: flex;
              justify-content: center;
              align-items: center;
              background-color: #041156;
              border: 2px solid #e8e8e8;
              border-radius: 0 8px 8px 0;

              ${mobile(({ setAutomaticDesignBasedSize }) => css`
                width: ${setAutomaticDesignBasedSize(80, 70)};
              `)}

              ${isSearchButtonDisabled && css`
                background-color: #e8e8e8;
              `}
            `}
          >
            <span
              css={css`
                font-size: 0;
                display: block;
                width: 30px;

                svg {
                  fill: #fff;

                  ${isSearchButtonDisabled && css`
                    fill: #b9b9b9;
                  `}
                }
              `}
            >
              <MagnifyingGlassSvg />
            </span>
          </button>
        </div>
      </div>
      <dialog
        id="mainvisualFreeWordSearchWidget-modal"
        ref={dialogModalRef}
        onClick={overlayClickHandler}
        css={css`
          width: fit-content;
          margin: auto;
          padding: 1em;
          overflow: visible;
          inset: 0;
          transition: opacity ${setTime(0.3)}s, scale ${setTime(0.3)}s;

          &::backdrop {
            background: linear-gradient(to bottom, rgb(0 0 0 / 0.8) 60%, rgb(0 0 0 / 0.4) 90%);
            transition: opacity ${setTime(0.3)}s;
          }

          &:not([data-activate="true"]) {
            opacity: 0;
            scale: 0.95;

            &::backdrop {
              opacity: 0;
            }
          }
        `}
      >
        <div
          css={css`
            width: 780px;
            padding: 36px 40px 20px;
            background-color: #e7fbfb;
            border-radius: 20px;
            position: relative;

            ${tablet(({ setAutomaticDesignBasedSize }) => css`
              width: 100%;
              max-width: 500px;
              padding: ${setAutomaticDesignBasedSize(36, 23)} ${setAutomaticDesignBasedSize(40, 12)} ${setAutomaticDesignBasedSize(20, 18)};
            `)}

            ${mobile(({ setAutomaticDesignBasedSize }) => css`
              padding: ${setAutomaticDesignBasedSize(36, 23)} ${setAutomaticDesignBasedSize(40, 12)} ${setAutomaticDesignBasedSize(20, 18)};
            `)}
          `}
        >
          <div
            css={css`
              width: fit-content;
              margin-inline: auto;
              margin-bottom: 30px;
              position: relative;

              ${tablet(({ setAutomaticDesignBasedSize }) => css`
                margin-bottom: ${setAutomaticDesignBasedSize(40, 28)};
              `)}

              ${mobile(({ setAutomaticDesignBasedSize }) => css`
                margin-bottom: ${setAutomaticDesignBasedSize(40, 28)};
              `)}
            `}
          >
            <p
              css={css`
                color: #041156;
                font-size: 22px;
                font-weight: bold;

                ${tablet(({ setAutomaticDesignBasedSize }) => css`
                  font-size: ${setAutomaticDesignBasedSize(22, 15)};
                `)}

                ${mobile(({ setAutomaticDesignBasedSize }) => css`
                  font-size: ${setAutomaticDesignBasedSize(22, 15)};
                `)}
              `}
            >
              気になるキーワードを選択してください
            </p>
            <p
              css={css`
                color: #727272;
                font-size: 14px;
                font-weight: bold;
                white-space: nowrap;
                position: absolute;
                top: 50%;
                left: calc(100% + 8px);
                transform: translateY(-50%);

                ${tablet(({ setAutomaticDesignBasedSize }) => css`
                  font-size: ${setAutomaticDesignBasedSize(14, 11)};
                  top: calc(100% + 8px);
                  left: auto;
                  right: 0;
                `)}

                ${mobile(({ setAutomaticDesignBasedSize }) => css`
                  font-size: ${setAutomaticDesignBasedSize(14, 11)};
                `)}
              `}
            >
              ※複数選択可
            </p>
          </div>
          <div
            css={css`
              display: flex;
              flex-wrap: wrap;
              gap: 20px;
              margin-bottom: 50px;
              padding: 0 12px;

              ${tablet(({ setAutomaticDesignBasedSize }) => css`
                gap: ${setAutomaticDesignBasedSize(20, 11)} ${setAutomaticDesignBasedSize(20, 12)};
                margin-bottom: ${setAutomaticDesignBasedSize(50, 23)};
                padding: 0;
              `)}

              ${mobile(({ setAutomaticDesignBasedSize }) => css`
                gap: ${setAutomaticDesignBasedSize(20, 11)} ${setAutomaticDesignBasedSize(20, 12)};
                margin-bottom: ${setAutomaticDesignBasedSize(50, 23)};
              `)}
            `}
          >
            {freeWordList.map((freeWord, index) => {
              const { id, value, checked } = freeWord;

              return (
                <div
                  key={index}
                  css={css`
                  `}
                >
                  <input
                    type="checkbox"
                    id={`freeWord-${index}`}
                    name={`freeWord-${index}`}
                    value={value}
                    checked={checked}
                    onChange={(event) => {
                      // GAイベント送信
                      sendGa4Event(KEY_NAME, `ga_event_${KEY_NAME}_${event.currentTarget?.checked ? 'check' : 'uncheck'}_${id}`);

                      // チェックボックスの状態更新
                      freeWordChangeHandler(event)
                    }}
                    css={css`
                      display: none;
                    `}
                  />
                  <label
                    htmlFor={`freeWord-${index}`}
                    css={css`
                      color: #041156;
                      font-size: 16px;
                      font-weight: bold;
                      padding: 9px 14px 9px 40px;
                      background-color: #fff;
                      border-radius: 6px;
                      position: relative;

                      ${mobile(({ setAutomaticDesignBasedSize }) => css`
                        font-size: ${setAutomaticDesignBasedSize(16, 14)};
                        padding: ${setAutomaticDesignBasedSize(9, 7)} ${setAutomaticDesignBasedSize(14, 14)} ${setAutomaticDesignBasedSize(9, 7)} ${setAutomaticDesignBasedSize(40, 37)};
                      `)}

                      &::before,
                      &::after {
                        content: "";
                        position: absolute;
                        top: 50%;
                        transform: translateY(-50%);
                      }

                      &::before {
                        width: 20px;
                        height: 20px;
                        background-color: #fff;
                        border: 1px solid #cecece;
                        border-radius: 3px;
                        left: 14px;

                        ${mobile(() => css`
                          left: 7px;
                        `)}
                      }

                      &::after {
                        width: 12px;
                        height: 9px;
                        background: url("data:image/svg+xml;charset=utf8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2211.805%22%20height%3D%228.803%22%20viewBox%3D%220%200%2011.805%208.803%22%3E%3Cpath%20d%3D%22M4.01%2C13.208.173%2C9.371a.59.59%2C0%2C0%2C1%2C0-.835L1.008%2C7.7a.59.59%2C0%2C0%2C1%2C.835%2C0l2.585%2C2.585L9.963%2C4.75a.59.59%2C0%2C0%2C1%2C.835%2C0l.835.835a.59.59%2C0%2C0%2C1%2C0%2C.835L4.844%2C13.208A.59.59%2C0%2C0%2C1%2C4.01%2C13.208Z%22%20transform%3D%22translate(0%20-4.577)%22%20fill%3D%22%23041156%22%2F%3E%3C%2Fsvg%3E") no-repeat center center / contain;
                        left: 18px;
                        opacity: 0;

                        ${mobile(() => css`
                          left: 11px;
                        `)}
                      }

                      ${checked && css`
                        background-color: #fff7c9;

                        &::before {
                          background-color: #ffd803;
                        }

                        &::after {
                          opacity: 1;
                        }
                      `}
                    `}
                  >
                    {value}
                  </label>
                </div>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => {
              setSelectedFreeWordList(freeWordList.filter((freeWord) => freeWord.checked).map((freeWord) => freeWord.value));
              closeModalHandler();
            }}
            id={`ga_event_${KEY_NAME}_confirm_button`}
            css={css`
              color: #fff;
              font-size: 22px;
              font-weight: bold;
              text-align: center;
              max-width: 153px;
              margin-inline: auto;
              padding: 19px 0;
              background-color: #041156;
              border-radius: 8px;

              ${tablet(({ setAutomaticDesignBasedSize }) => css`
                font-size: ${setAutomaticDesignBasedSize(16, 14)};
              `)}

              ${mobile(({ setAutomaticDesignBasedSize }) => css`
                font-size: ${setAutomaticDesignBasedSize(16, 14)};
              `)}

              &:hover {
                color: #041156;
                background-color: #ffd803;
              }
            `}
          >
            決定
          </button>
          <div
            css={css`
              position: absolute;
              bottom: calc(100% + 8px);
              right: 0;
            `}
          >
            <button
              type="button"
              onClick={closeModalHandler}
              css={css`
                display: flex;
                justify-content: center;
                align-items: center;
                width: 48px;
                height: 48px;
                background-color: #d6d6d6;
                border-radius: 50%;

                &:hover {
                  background-color: #ccc;
                }
              `}
            >
              <span
                css={css`
                  font-size: 0;
                  width: 21px;
                `}
              >
                <CrossMarkSvg />
              </span>
            </button>
          </div>
        </div>
      </dialog>
    </div>
  );
};
